import { useEffect, useMemo, useState } from "react";
import { getRecentSearches } from "@/lib/recent-searches";
import { expandQuery } from "@/lib/search-synonyms";
import { normalizeText } from "@/lib/search-rank";
import { buildIndex, sortIndexed, unionSortIndexed } from "../lib/search-index";
import { recordIndexSize, measureSearch } from "../lib/search-metrics";
// Side-effect: registers all providers into the registry
import { registry } from "../providers";
import { useSearchCorpus } from "./useSearchCorpus";

/* ─────────────────────────────────────────
   COMMANDS PALETTE — extend here
───────────────────────────────────────── */
const QUICK_COMMANDS = [
  // Navigation — main sections
  { keyword: "inicio",        title: "Ir al inicio",           subtitle: "Dashboard principal",          route: "/" },
  { keyword: "distrito",      title: "Abrir Distrito",          subtitle: "Hub administrativo",           route: "/district" },
  { keyword: "agenda",        title: "Ver Agenda",              subtitle: "Eventos y actividades",        route: "/agenda" },
  { keyword: "ajustes",       title: "Configuración",           subtitle: "Ajustes de la app",            route: "/settings" },
  { keyword: "notas",         title: "Ver Notas",               subtitle: "Notas pastorales",             route: "/notes" },
  // Navigation — entity lists
  { keyword: "personas",      title: "Ver Personas",            subtitle: "Directorio del distrito",      route: "/families" },
  { keyword: "hogares",       title: "Ver Hogares",             subtitle: "Familias del distrito",        route: "/families" },
  { keyword: "iglesias",      title: "Ver Iglesias",            subtitle: "Iglesias del distrito",        route: "/churches" },
  { keyword: "visitas",       title: "Ver Visitas",             subtitle: "Seguimiento pastoral",         route: "/visits" },
  { keyword: "oraciones",     title: "Ver Oraciones",           subtitle: "Peticiones de oración",        route: "/prayers" },
  { keyword: "predicacion",   title: "Ver Predicación",         subtitle: "Predicaciones",                route: "/preaching" },
  { keyword: "materiales",    title: "Ver Materiales",          subtitle: "Inventario del distrito",      route: "/materials" },
  { keyword: "bautismos",     title: "Ver Bautismos",           subtitle: "Registros de bautismo",        route: "/baptisms" },
  { keyword: "organigrama",   title: "Organigrama",             subtitle: "Junta directiva y cargos",     route: "/churches" },
  { keyword: "junta",         title: "Junta Directiva",         subtitle: "Organigrama del distrito",     route: "/churches" },
  { keyword: "revision",      title: "Revisión mensual",        subtitle: "Verificación mensual",         route: "/administrative-review" },
  // Create actions
  { keyword: "nueva visita",  title: "Crear visita",            subtitle: "Agregar seguimiento pastoral", route: "/visits/new" },
  { keyword: "nuevo hogar",   title: "Crear hogar",             subtitle: "Registrar nuevo hogar",        route: "/families/new" },
  { keyword: "nuevo evento",  title: "Crear evento",            subtitle: "Agregar actividad ministerial",route: "/agenda/field/new" },
  { keyword: "nueva oracion", title: "Agregar oración",         subtitle: "Registrar petición",           route: "/prayers/new" },
];

/* ─────────────────────────────────────────
   HOOK
───────────────────────────────────────── */
/**
 * @param {{ districtId: string }} options
 * @returns {import("../lib/types").SearchDataResult}
 */
export function useSearchData({ districtId }) {
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState([]);

  // ── Corpus: memory + Dexie + SWR ─────────────────────────────────────────
  // First open: loading = true until network fetch completes.
  // Every subsequent open within the session: loading = false instantly (memory cache).
  // After page reload: loading = false after ~5 ms Dexie Phase 0 hydration.
  const { rawCorpus, loading } = useSearchCorpus(districtId);

  useEffect(() => {
    setRecent(getRecentSearches());
  }, []);

  // ── Index: rebuilt only when corpus refreshes, never on keystrokes ────────
  const indexedData = useMemo(() => {
    if (!rawCorpus) return {};
    const providers = registry.getAll();
    const result = {};
    let totalItems = 0;
    for (const p of providers) {
      const items = rawCorpus[p.id] || [];
      result[p.id] = buildIndex(items, p.fields);
      totalItems += items.length;
    }
    recordIndexSize(totalItems);
    return result;
  }, [rawCorpus]);

  const indexState = rawCorpus ? "ready" : "loading";

  const q = query.trim();

  // ── Per-type filtering — all local, zero network ──────────────────────────

  const filteredPeople = useMemo(() => {
    const idx = indexedData.personas;
    if (!idx || !q) return [];
    return measureSearch(q, () => sortIndexed(idx, q));
  }, [indexedData, q]);

  const filteredFamilies = useMemo(() => {
    const idx = indexedData.familias;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const filteredChurches = useMemo(() => {
    const idx = indexedData.iglesias;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const filteredVisits = useMemo(() => {
    const idx = indexedData.visitas;
    if (!idx || !q) return [];
    return unionSortIndexed(idx, expandQuery(q));
  }, [indexedData, q]);

  const filteredPrayers = useMemo(() => {
    const idx = indexedData.oraciones;
    if (!idx || !q) return [];
    return unionSortIndexed(idx, expandQuery(q));
  }, [indexedData, q]);

  // Events now come from the provider registry (eventsProvider merges all types)
  const filteredEvents = useMemo(() => {
    const idx = indexedData.eventos;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const filteredNotes = useMemo(() => {
    const idx = indexedData.notas;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const filteredPreaching = useMemo(() => {
    const idx = indexedData.predicacion;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const filteredMaterials = useMemo(() => {
    const idx = indexedData.materiales;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const filteredBaptisms = useMemo(() => {
    const idx = indexedData.bautismos;
    if (!idx || !q) return [];
    return sortIndexed(idx, q);
  }, [indexedData, q]);

  const matchedCommands = useMemo(() => {
    if (!q) return [];
    const qNorm = normalizeText(q);
    return QUICK_COMMANDS.filter(
      (cmd) =>
        cmd.keyword.includes(qNorm) ||
        normalizeText(cmd.title).includes(qNorm) ||
        (cmd.subtitle && normalizeText(cmd.subtitle).includes(qNorm))
    );
  }, [q]);

  const totalResults =
    filteredPeople.length +
    filteredFamilies.length +
    filteredChurches.length +
    filteredVisits.length +
    filteredEvents.length +
    filteredPrayers.length +
    filteredNotes.length +
    filteredPreaching.length +
    filteredMaterials.length +
    filteredBaptisms.length;

  return {
    query,
    setQuery,
    loading,
    indexState,
    recent,
    filteredPeople,
    filteredFamilies,
    filteredChurches,
    filteredVisits,
    filteredEvents,
    filteredPrayers,
    filteredNotes,
    filteredPreaching,
    filteredMaterials,
    filteredBaptisms,
    matchedCommands,
    totalResults,
  };
}
